import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { submitHeroesRequest } from "@/lib/api";

const GROUPS = [
    { value: "veteran", label: "Veteran" },
    { value: "active-duty", label: "Active-Duty Military" },
    { value: "police", label: "Police Officer" },
    { value: "firefighter", label: "Firefighter" },
    { value: "emt", label: "EMT" },
    { value: "paramedic", label: "Paramedic" },
];

const MAX_MB = 8;

export default function HeroesModal({ onClose }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        group: "",
        message: "",
    });
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const closeBtnRef = useRef(null);

    useEffect(() => {
        closeBtnRef.current?.focus();
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const canSubmit =
        form.name.trim() &&
        form.email.trim() &&
        form.phone.trim() &&
        form.group &&
        file &&
        !submitting;

    async function onSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File must be under ${MAX_MB} MB.`);
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("email", form.email);
            fd.append("phone", form.phone);
            fd.append("group", form.group);
            if (form.message) fd.append("message", form.message);
            fd.append("document", file);
            await submitHeroesRequest(fd);
            setSuccess(true);
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Could not submit your request. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="heroes-modal-title"
            data-testid="heroes-modal"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="relative max-h-[95vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8">
                <button
                    ref={closeBtnRef}
                    type="button"
                    onClick={onClose}
                    data-testid="heroes-modal-close"
                    aria-label="Close verification form"
                    className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-navy)]"
                >
                    <X className="h-5 w-5" />
                </button>

                {success ? (
                    <div
                        data-testid="heroes-submitted"
                        className="py-6 text-center"
                    >
                        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[color:var(--brand-green)] text-white">
                            <CheckCircle2 className="h-8 w-8" />
                        </span>
                        <h2 className="mt-5 font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                            Thank you for your service.
                        </h2>
                        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-slate-600">
                            We will review your request and send your private
                            $20 discount code by email or text before checkout.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="heroes-submitted-close"
                            className="hero-cta pill-btn mt-8 inline-flex text-white"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        className="pr-4"
                        data-testid="heroes-form"
                    >
                        <div className="text-xs font-bold uppercase tracking-widest text-[color:var(--brand-green-2)]">
                            Heroes Discount · $20 off
                        </div>
                        <h2
                            id="heroes-modal-title"
                            className="mt-1 font-display text-2xl font-extrabold text-[color:var(--brand-navy)] sm:text-3xl"
                        >
                            Verify your eligibility
                        </h2>
                        <p className="mt-2 text-sm text-slate-600">
                            Quick form — usually reviewed within one business
                            day. All fields required unless marked optional.
                        </p>

                        <div className="mt-6 grid gap-4">
                            <div>
                                <Label className="mb-2 block text-sm font-semibold text-slate-800">
                                    Full name
                                </Label>
                                <Input
                                    data-testid="heroes-name-input"
                                    value={form.name}
                                    onChange={set("name")}
                                    required
                                    className="h-12 rounded-xl text-base"
                                />
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <Label className="mb-2 block text-sm font-semibold text-slate-800">
                                        Email
                                    </Label>
                                    <Input
                                        data-testid="heroes-email-input"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        required
                                        className="h-12 rounded-xl text-base"
                                    />
                                </div>
                                <div>
                                    <Label className="mb-2 block text-sm font-semibold text-slate-800">
                                        Phone
                                    </Label>
                                    <Input
                                        data-testid="heroes-phone-input"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        required
                                        className="h-12 rounded-xl text-base"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-2 block text-sm font-semibold text-slate-800">
                                    Eligible group
                                </Label>
                                <Select
                                    value={form.group}
                                    onValueChange={(v) =>
                                        setForm((f) => ({ ...f, group: v }))
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="heroes-group-select"
                                        className="h-12 rounded-xl text-base"
                                    >
                                        <SelectValue placeholder="Select your group" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {GROUPS.map((g) => (
                                            <SelectItem
                                                key={g.value}
                                                value={g.value}
                                                data-testid={`heroes-group-${g.value}`}
                                            >
                                                {g.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-2 block text-sm font-semibold text-slate-800">
                                    Proof of eligibility
                                </Label>
                                <label
                                    htmlFor="heroes-file"
                                    className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 hover:border-[color:var(--brand-navy)]"
                                >
                                    <Upload className="h-5 w-5 text-[color:var(--brand-navy)]" />
                                    <span data-testid="heroes-file-label">
                                        {file
                                            ? file.name
                                            : "Upload a photo of your ID (JPG, PNG, or PDF, up to 8 MB)"}
                                    </span>
                                    <input
                                        id="heroes-file"
                                        data-testid="heroes-file-input"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,application/pdf"
                                        onChange={(e) =>
                                            setFile(e.target.files?.[0] || null)
                                        }
                                        className="hidden"
                                    />
                                </label>
                                <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>
                                        Please cover Social Security numbers,
                                        addresses, identification numbers, and
                                        other sensitive information before
                                        uploading. We only need to see your
                                        name and proof of eligibility. Uploaded
                                        documents are stored securely and
                                        deleted after verification.
                                    </span>
                                </div>
                            </div>
                            <div>
                                <Label className="mb-2 block text-sm font-semibold text-slate-800">
                                    Message (optional)
                                </Label>
                                <Textarea
                                    data-testid="heroes-message-input"
                                    value={form.message}
                                    onChange={set("message")}
                                    rows={3}
                                    className="rounded-xl text-base"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="heroes-submit-button"
                            disabled={!canSubmit}
                            className="hero-cta pill-btn mt-6 inline-flex w-full items-center justify-center gap-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-navy)]"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Submitting…
                                </>
                            ) : (
                                "Submit for Verification"
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
